import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import { api } from '../lib/api';
import { prerenderedPost } from '../lib/prerender';
import { useScrollDepth } from '../hooks/useScrollDepth';

// Simple markdown-ish renderer for headings, lists, bold
const renderContent = (content = '') => {
  const lines = content.split('\n');
  const out = [];
  let list = null;
  const flushList = () => {
    if (list) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-6 mb-4 space-y-2">
          {list.map((li, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: li }} />
          ))}
        </ul>
      );
      list = null;
    }
  };
  const bold = (s) => s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (line.startsWith('## ')) {
      flushList();
      out.push(<h2 key={idx}>{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      flushList();
      out.push(<h3 key={idx}>{line.slice(4)}</h3>);
    } else if (line.match(/^\d+\.\s+/) || line.startsWith('- ')) {
      const item = line.replace(/^\d+\.\s+|- /, '');
      if (!list) list = [];
      list.push(bold(item));
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      out.push(<p key={idx} dangerouslySetInnerHTML={{ __html: bold(line) }} />);
    }
  });
  flushList();
  return out;
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const initial = prerenderedPost(slug);
  const [post, setPost] = useState(initial);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(false);
  useScrollDepth(post ? `/blog/${post.slug}` : null, { content_type: 'blog_post' });

  useEffect(() => {
    // Skip the fetch when this slug was prerendered and is already on screen
    if (prerenderedPost(slug) && post?.slug === slug) return;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await api.get(`/blog/${slug}`);
        setPost(res.data);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    (async () => {
      if (!post) return;
      try {
        const res = await api.get('/blog');
        const others = res.data.filter((p) => p.slug !== post.slug);
        // score by shared tags (higher = more related), fall back to newest
        const scored = others
          .map((p) => ({
            p,
            score: (p.tags || []).filter((t) => (post.tags || []).includes(t)).length,
          }))
          .sort((a, b) => b.score - a.score || new Date(b.p.created_at) - new Date(a.p.created_at))
          .map((x) => x.p);
        setRelated(scored.slice(0, 3));
      } catch (e) {
        // ignore
      }
    })();
  }, [post]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40 text-ink-3">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="section text-center">
        <SEO title="Post not found" path={`/blog/${slug}`} noindex />
        <h1 className="headline-lg text-ink mb-4">Post not found</h1>
        <Link to="/blog" className="btn-link"><ArrowLeft className="w-4 h-4" /> Back to blog</Link>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        publishedTime={post.created_at}
        modifiedTime={post.updated_at}
        author={post.author}
        keywords={post.tags}
        image={post.cover_image || undefined}
        jsonLd={post.json_ld_extra || undefined}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: post.title, path: `/blog/${post.slug}` },
        ]}
        article={{
          headline: post.title,
          description: post.seo_description || post.excerpt,
          image: post.cover_image || undefined,
          datePublished: post.created_at,
          dateModified: post.updated_at,
          author: post.author,
          tags: post.tags || [],
        }}
      />

      <article className="bg-white pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-[11px] text-ink-3 mb-6">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-ink transition-colors">Home</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link to="/blog" className="hover:text-ink transition-colors">Blog</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-ink-2 line-clamp-1 max-w-[240px]">{post.title}</li>
            </ol>
          </nav>

          <Link to="/blog" className="btn-link mb-8 inline-flex"><ArrowLeft className="w-4 h-4" /> All posts</Link>
          <div className="flex flex-wrap gap-2 mb-4">
            {(post.tags || []).map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-widest text-ink-3">{t}</span>
            ))}
          </div>
          <h1 className="headline-lg text-ink mb-6">{post.title}</h1>
          <div className="text-sm text-ink-3 mb-10">
            <time dateTime={post.created_at}>
              {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </time>
            {' · '}
            <span>{post.author}</span>
          </div>
          {post.cover_image && (
            <img src={post.cover_image} alt={post.alt_text || post.title} loading="eager" decoding="async" width="1200" height="750" className="w-full rounded-2xl mb-10 border border-[var(--line-2)]" />
          )}
          <div className="prose">{renderContent(post.content)}</div>
        </div>
      </article>

      {/* Related posts — internal linking for SEO */}
      {related.length > 0 && (
        <aside className="bg-[var(--bg-alt)] section" aria-labelledby="related-heading">
          <div className="max-w-6xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">Keep reading</div>
            <h2 id="related-heading" className="headline-md text-ink mb-10">Related posts</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-analytics="blog_related_click"
                  data-label={p.slug}
                  className="group bg-white rounded-2xl border border-[var(--line-2)] overflow-hidden lift"
                >
                  {p.cover_image ? (
                    <img
                      src={p.cover_image}
                      alt={p.alt_text || p.title}
                      loading="lazy"
                      decoding="async"
                      width="600"
                      height="375"
                      className="w-full aspect-[16/10] object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-[16/10] bg-[var(--bg-alt)]" />
                  )}
                  <div className="p-5">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(p.tags || []).slice(0, 2).map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-widest text-ink-3">{t}</span>
                      ))}
                    </div>
                    <h3 className="text-base font-semibold text-ink leading-snug group-hover:underline">{p.title}</h3>
                    <div className="mt-3 text-[11px] text-ink-3">
                      {new Date(p.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} · {p.author}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      )}
    </>
  );
};

export default BlogPostPage;
